/**
 * Session Data Cleanup Utility
 *
 * Provides a centralized function to clear all session-related data
 * including cookies, localStorage, sessionStorage, and cache.
 */

// Type definition for Cookie Store API
interface CookieStore {
  delete(options: { name: string; domain?: string; path?: string }): Promise<void>;
}

/**
 * Deletes a cookie using Cookie Store API or fallback to document.cookie
 */
const deleteCookie = async (name: string, domain?: string): Promise<void> => {
  // Try modern Cookie Store API first (if available)
  if ('cookieStore' in window) {
    try {
      await (window as typeof window & { cookieStore: CookieStore }).cookieStore.delete({
        name,
        domain,
        path: '/',
      });
      return;
    } catch {
      // Fallback to document.cookie if Cookie Store API fails
    }
  }

  // Fallback: Use document.cookie for older browsers
  const cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;${domain ? ` domain=${domain};` : ''}`;
  const cookieDescriptor = Object.getOwnPropertyDescriptor(Document.prototype, 'cookie');

  if (cookieDescriptor?.set) {
    cookieDescriptor.set.call(document, cookieString);
  }
};

/**
 * Clears all session-related data from browser storage
 * This includes:
 * - All cookies
 * - localStorage items
 * - sessionStorage items
 * - Any cached data
 */
export const clearAllSessionData = async (): Promise<void> => {
  try {
    // Clear localStorage
    localStorage.clear();

    // Clear sessionStorage
    sessionStorage.clear();

    // Clear all cookies
    const cookies = document.cookie.split(';');
    const domain = window.location.hostname;

    for (const cookie of cookies) {
      const [name] = cookie.split('=');
      const trimmedName = name.trim();

      // Try multiple variants to ensure complete cleanup
      await deleteCookie(trimmedName);
      await deleteCookie(trimmedName, domain);
      await deleteCookie(trimmedName, `.${domain}`);
    }

    // Clear any service worker caches if available
    if ('caches' in window) {
      caches.keys().then((names) => {
        for (const name of names) {
          caches.delete(name);
        }
      });
    }
  } catch (error) {
    console.error('Error clearing session data:', error);
  }
};
